/* eslint-disable no-console */

/** 上次确认已跑在当前页上的线上 buildId（仅 embedded === remote 后写入） */
export const STORAGE_KEY = 'ds-app-build-id';
/** 本会话已对某 remote 做过一次硬换壳，防止入口仍旧时死循环 */
export const RELOAD_GUARD_KEY = 'ds-app-build-reload';

const SW_PING_INTERVAL_MS = 30 * 60 * 1000;
/** 启动探测最多等这么久；超时后不挡挂载、不自动换壳 */
export const BOOT_PROBE_TIMEOUT_MS = 800;
const FETCH_TIMEOUT_MS = 4000;

let updateChecksStarted = false;
let shellReplaceInFlight = false;

/**
 * 纯决策：发现新版本时做什么。
 * confirm = 已对齐；defer = 等用户下次主动刷新；hard-refresh = 本次就是用户刷新；
 * give-up = 本会话已换过仍旧，不再自动刷。
 *
 * @param {{ remote?: string, embedded?: string, prev?: string, guard?: string, allowHardRefresh?: boolean }} input
 */
export function resolveAppBuildSyncAction(input = {}) {
  const remote = String(input.remote || '').trim();
  const embedded = String(input.embedded || '').trim();
  const prev = String(input.prev || '').trim();
  const guard = String(input.guard || '').trim();
  const allowHardRefresh = Boolean(input.allowHardRefresh);

  if (!remote) return { type: 'noop' };

  const mismatch = (prev && prev !== remote) || (embedded && embedded !== remote);
  if (!mismatch) return { type: 'confirm' };

  if (allowHardRefresh) {
    if (guard === remote) return { type: 'give-up' };
    return { type: 'hard-refresh' };
  }
  return { type: 'defer' };
}

export function getEmbeddedBuildId() {
  return String(process.env.VUE_APP_BUILD_ID || '').trim();
}

export function parseRemoteBuildId(data) {
  return String(data?.buildId || '').trim();
}

export async function fetchRemoteBuildId() {
  const base = process.env.BASE_URL || '/';
  const url = `${base}version.json?_=${Date.now()}`;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : null;
  try {
    const res = await fetch(url, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      ...(ctrl ? { signal: ctrl.signal } : {}),
    });
    if (!res.ok) return '';
    const data = await res.json().catch(() => null);
    return parseRemoteBuildId(data);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function readSessionKeys() {
  let prev = '';
  let guard = '';
  try {
    prev = String(localStorage.getItem(STORAGE_KEY) || '').trim();
  } catch {
    /* ignore */
  }
  try {
    guard = String(sessionStorage.getItem(RELOAD_GUARD_KEY) || '').trim();
  } catch {
    /* ignore */
  }
  return { prev, guard };
}

function persistConfirmedBuild(remote) {
  try {
    localStorage.setItem(STORAGE_KEY, remote);
  } catch {
    /* ignore */
  }
}

function persistReloadGuard(remote) {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, remote);
  } catch {
    /* ignore */
  }
}

export async function clearAppShellCaches() {
  if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((reg) => reg.unregister().catch(() => false)));
  }
  if (typeof caches !== 'undefined' && caches.keys) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key).catch(() => false)));
  }
}

/** 卸 SW + 清 Cache 后再导航；普通 location.reload() 仍会打到旧 precache。 */
export async function applyHardShellRefresh(remote = '') {
  if (typeof window === 'undefined') return;
  if (shellReplaceInFlight) return;
  shellReplaceInFlight = true;
  const id = String(remote || '').trim();
  if (id) persistReloadGuard(id);
  try {
    await clearAppShellCaches();
  } catch {
    /* 仍尝试离开当前壳 */
  }
  window.location.replace(window.location.href);
}

/**
 * @param {string} remote
 * @param {boolean} allowHardRefresh
 * @returns {Promise<{ mismatch: boolean, reloading: boolean, remote: string, type: string }>}
 */
async function applyInspectedBuild(remote, allowHardRefresh) {
  const id = String(remote || '').trim();
  if (!id) {
    return { mismatch: false, reloading: false, remote: '', type: 'noop' };
  }

  const embedded = getEmbeddedBuildId();
  const { prev, guard } = readSessionKeys();
  const action = resolveAppBuildSyncAction({
    remote: id,
    embedded,
    prev,
    guard,
    allowHardRefresh,
  });

  if (action.type === 'confirm') {
    persistConfirmedBuild(id);
    return { mismatch: false, reloading: false, remote: id, type: action.type };
  }

  if (action.type === 'hard-refresh') {
    console.info('[build-sync] new build detected, replacing shell…', {
      prev,
      embedded,
      remote: id,
    });
    await applyHardShellRefresh(id);
    return { mismatch: true, reloading: true, remote: id, type: action.type };
  }

  if (action.type === 'give-up') {
    console.warn(
      '[build-sync] version mismatch persists after shell replace; continue with current bundle'
    );
  }

  return { mismatch: true, reloading: false, remote: id, type: action.type };
}

/**
 * 启动闸：只对 version.json 短超时。
 * 超时不挡挂载、不换壳；仅用户本次主动打开/刷新且超时前确认旧壳才硬换壳。
 * @returns {Promise<{ mismatch: boolean, reloading: boolean, remote: string, timedOut?: boolean }>}
 */
export async function probeAppBuildAtBoot() {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') {
    return { mismatch: false, reloading: false, remote: '' };
  }

  const fetchPromise = fetchRemoteBuildId()
    .then((remote) => ({ remote }))
    .catch((err) => {
      console.warn('[build-sync] version check failed', err);
      return { remote: '' };
    });

  const raced = await Promise.race([
    fetchPromise,
    new Promise((resolve) => {
      window.setTimeout(() => resolve('timeout'), BOOT_PROBE_TIMEOUT_MS);
    }),
  ]);

  if (raced === 'timeout') {
    return { mismatch: false, reloading: false, remote: '', timedOut: true };
  }

  return applyInspectedBuild(raced.remote, true);
}

function pingServiceWorkerUpdate() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) return;
  navigator.serviceWorker
    .getRegistration()
    .then((reg) => reg?.update?.())
    .catch(() => {});
}

/** 后台催 SW 下载新包，等用户下次主动刷新再换壳。 */
export function startServiceWorkerUpdateChecks() {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;
  if (updateChecksStarted) return;
  updateChecksStarted = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    pingServiceWorkerUpdate();
  });

  window.setInterval(pingServiceWorkerUpdate, SW_PING_INTERVAL_MS);
}
